import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    setGeneralStep,
    setGlassReplacement,
    setHelpType,
} from "./helpTypeSlice";
import { JSX, useCallback, useEffect, useRef } from "react";
import { SvgDamageIcon } from "../../common/components/svgIcons/SvgDamageIcon";
import { SvgHailIcon } from "../../common/components/svgIcons/SvgHailIcon";
import { SvgGlassIcon } from "../../common/components/svgIcons/SvgGlassIcon";
import { SvgCamper } from "../../common/components/svgIcons/SvgCamper";
import { GeneralStepsEnum } from "../../common/constants/GeneralStepsEnum";
import { updateDossier } from "../../app/rootReducer";
import { PatchStepsEnum } from "../../common/constants/PatchStepsEnum";
import {
    setFundingType,
    unsetDamageCause,
    unsetGlassDamage,
} from "../vehicleInformation/vehicleInformationSlice";
import { unsetAppointment } from "../appointment/appointmentSlice";
import { dossierTypeEnum } from "../../common/constants/dossierTypeEnum";
import { fundingTypeEnum } from "../../common/constants/fundingTypeEnum";
import { useSearchParams } from "react-router-dom";
import { scheduleScrollToStep } from "../../common/helpers/scrollToStep";
import { helpTypes } from "./helpType.config";
import { helpTypeCopy } from "./helpType.copy";

export const HelpTypeSelect = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { registrationLink } = useAppSelector((state) => state.root);

    const onClick = useCallback(
        (helpType: { value: string }): void => {
            dispatch(setHelpType(helpType.value));
            dispatch(setGeneralStep(GeneralStepsEnum.TypeSelect));
            dispatch(unsetDamageCause());
            dispatch(unsetGlassDamage());
            dispatch(setFundingType(fundingTypeEnum.Unknown));
            dispatch(setGlassReplacement(false));
            dispatch(unsetAppointment());

            dispatch(
                updateDossier({
                    data: { appointment_type: helpType.value },
                    method: PatchStepsEnum.PatchAppointmentType,
                }),
            ).then(() => {
                if (
                    helpType.value === dossierTypeEnum.BodyRepair ||
                    helpType.value === dossierTypeEnum.CaravanRepair
                ) {
                    dispatch(setGeneralStep(GeneralStepsEnum.DamageCause));
                }
                if (
                    helpType.value === dossierTypeEnum.GlassRepair ||
                    helpType.value === dossierTypeEnum.GlassReplacement
                ) {
                    dispatch(setGeneralStep(GeneralStepsEnum.GlassDamage));
                }
                if (helpType.value === dossierTypeEnum.CaravanRepair) {
                    dispatch(setGeneralStep(GeneralStepsEnum.CaravanDamage));
                }
                if (helpType.value === dossierTypeEnum.HailDamage) {
                    if (
                        !registrationLink.private_lease &&
                        !registrationLink.business_lease
                    ) {
                        dispatch(setGeneralStep(GeneralStepsEnum.Insurance));
                        return;
                    }

                    dispatch(setGeneralStep(GeneralStepsEnum.Lease));
                }
            });
        },
        [
            dispatch,
            registrationLink.business_lease,
            registrationLink.private_lease,
        ],
    );

    const { activeHelpType } = useAppSelector((state) => state.helpType);
    const scrollRef = useRef<HTMLDivElement>(null);
    const getParamHelpType = searchParams.get("help-type");

    const visibleHelpTypes = helpTypes.filter(
        (helpType) =>
            !helpType.requiresHailMode || registrationLink.hail_damage_enabled,
    );

    useEffect(() => {
        scheduleScrollToStep(() => scrollRef.current);
    }, []);

    useEffect(() => {
        if (getParamHelpType) {
            onClick({ value: getParamHelpType });
        }

        window.dataLayer.push({
            event: "AanmeldenLinkType",
        });
    }, [getParamHelpType, onClick]);

    return (
        <div
            className="s-appointment__group border-color-primary"
            ref={scrollRef}
        >
            <h1 className="s-appointment__group__title">
                {helpTypeCopy.title}
            </h1>
            <div
                className={
                    "c-icon-button c-icon-button--row c-icon-button--horizontal" +
                    (visibleHelpTypes.length === 4
                        ? " c-icon-button--quad"
                        : "")
                }
            >
                {visibleHelpTypes.map((helpType) => {
                    let activeClass = "";
                    if (activeHelpType === helpType.value) {
                        activeClass = " button-active";
                    }
                    return (
                        <button
                            type="button"
                            key={"help-type-" + helpType.value}
                            onClick={() => onClick(helpType)}
                            className={
                                "c-icon-button__single border-color-primary border-color-primary-hover" +
                                activeClass
                            }
                        >
                            {helpType.value === dossierTypeEnum.HailDamage && (
                                <SvgHailIcon />
                            )}
                            {helpType.value === dossierTypeEnum.BodyRepair && (
                                <SvgDamageIcon />
                            )}
                            {(helpType.value === dossierTypeEnum.GlassRepair ||
                                helpType.value ===
                                    dossierTypeEnum.GlassReplacement) && (
                                <SvgGlassIcon />
                            )}
                            {helpType.value ===
                                dossierTypeEnum.CaravanRepair && <SvgCamper />}

                            <span className="font-face-primary">
                                {helpType.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
